import React, {useEffect} from 'react';
import {Box, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Outlet, useNavigate, useParams} from 'react-router-dom';
import {useEffectFn} from '@ngneat/effects-hooks';
import {useObservable} from '@ngneat/react-rxjs';
import {emitOnce} from '@ngneat/elf';
import {GridCellParams, GridValueGetterParams} from '@mui/x-data-grid-pro';

import {searchClientsServicesEffect} from '@store/clients/services/clients-services.effects';
import {ClientsService, clientsServicesQuery, clientsServicesService} from '@store/clients/services';

import SearchComponent from '@components/inputs/search/Search.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import NewTableComponent from '@components/table/NewTable.component';

import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {BlackCheck, GreyCheck} from '@assets/icons';
import {useSnackbar} from 'notistack';


const ClientContractedServicesTable = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {clientId} = useParams();

  const {enqueueSnackbar} = useSnackbar();

  const searchClientsServices = useEffectFn(searchClientsServicesEffect);

  const [{clientsServices, loading}] = useObservable(clientsServicesQuery.clientsServices$);
  const [selectedClientsServices] = useObservable(clientsServicesQuery.selectedClientsServices$);

  const [filters] = useObservable(clientsServicesQuery.filters$);
  const [sortModel] = useObservable(clientsServicesQuery.sort$);
  const [tablePagination] = useObservable(clientsServicesQuery.pagination$);

  useEffect(() => {
    if (clientId) clientsServicesService.resetFilters(clientId);
  }, [clientId]);

  useEffect(() => {
    searchClientsServices({filters, tablePagination, sort: sortModel});
  }, [filters, searchClientsServices, sortModel, tablePagination]);

  const handleEditService = (clientsService: ClientsService, canBeRequested: boolean) => {
    clientsServicesService.updateClientServiceById(clientsService.id, {
      ...clientsServices,
      buyPriceId: clientsService.buyPriceId,
      canBeRequested: canBeRequested
    })?.subscribe({
      next: () => {
        enqueueSnackbar(t('suppliers.details.catalog.manage.success.update'), {variant: 'success'});
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  return (
    <Stack flex={1} spacing={2} pb="25px">
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box width="500px">
          <SearchComponent
            value={filters.search}
            handleChange={(value) => {
              emitOnce(() => {
                clientsServicesService.setFilters('search')(value);
                clientsServicesService.setPagination('page')(1);
              });
            }}/>
        </Box>
        <TableFiltersComponent
          filters={filters}
          handleSave={clientsServicesService.setFilters('tableFilters')}
          handleReset={() => clientId ? clientsServicesService.resetFilters(clientId) : undefined}/>
      </Stack>
      <NewTableComponent
        loading={loading}
        columns={[
          COLUMN_TYPES.TEXT_BOLD({field: 'id', label: t('clients.servicesTable.id'), width: 100}),
          COLUMN_TYPES.TEXT_BOLD({
            field: 'service.clientWasteName',
            label: t('clients.servicesTable.name'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.clientWasteName,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.TEXT({
            field: 'schedulesCount',
            label: t('clients.servicesTable.frequency'),
            valueGetter: (params: GridValueGetterParams<any, ClientsService>) => (params.value ?? 0) > 0 ? t('global.frequencies.recurrent') : t('global.frequencies.onDemand'),
            width: 130,
          }),
          COLUMN_TYPES.TEXT({
            field: 'kind.kind',
            label: t('clients.servicesTable.type'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.kindName,
            width: 250,
          }),
          COLUMN_TYPES.TEXT({
            field: 'waste.name',
            label: t('clients.servicesTable.details'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.wasteName,
            width: 250,
          }),
          COLUMN_TYPES.TEXT({
            field: 'container.name',
            label: t('clients.servicesTable.container'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.containerName,
            width: 200,
          }),
          COLUMN_TYPES.TEXT({
            field: 'sOrgUnit.name',
            label: t('clients.servicesTable.supplier'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.supplierOrganisationUnitName,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.TEXT({
            field: 'cOrgUnit.name',
            label: t('clients.servicesTable.clientUnit'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.clientOrganisationUnitName,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.TEXT({
            field: 'buyPriceUnit',
            label: t('clients.servicesTable.purchasePrice'),
            valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.buyPriceIfOnlyOne !== null
              ? `${params.row.buyPriceIfOnlyOne / 100} ${params.row.buyPriceCurrency || ''}`
              : `${params.row.buyPricesCount || '-'}${params.row.buyPricesCount ? ' ' + t('suppliers.details.contractedServices.table.prices') : ''}`,
            width: 130,
          }),
          COLUMN_TYPES.TEXT({
            field: 'sellPriceUnit',
            label: t('clients.servicesTable.sellingPrice'),
            cellClassName: (params: GridCellParams<ClientsService>) => params.row.buyPriceIfOnlyOne && params.row.sellPriceIfOnlyOne && params.row.buyPriceIfOnlyOne > params.row.sellPriceIfOnlyOne ? 'error' : '',
            valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.sellPriceIfOnlyOne !== null
              ? `${params.row.sellPriceIfOnlyOne / 100} ${params.row.sellPriceCurrency || ''}`
              : `${params.row.sellPricesCount || '-'}${params.row.sellPricesCount ? ' ' + t('suppliers.details.contractedServices.table.prices') : ''}`,
            width: 130,
          }),
          COLUMN_TYPES.CHECKS({
            field: 'canBeRequested',
            label: t('clients.servicesTable.canBeRequested'),
            cellClassName: () => '',
            checks: [
              {
                isChecked: (service: ClientsService) => service.canBeRequested,
                checkedIcon: <BlackCheck/>,
                uncheckedIcon: <GreyCheck/>,
                handleClick: (service: ClientsService, checked: boolean) => {
                  handleEditService(service, checked);
                }
              },
            ],
            width: 150,
          }),
          COLUMN_TYPES.ACTIONS({handleShowRow: (clientsService: ClientsService) => navigate(`${clientsService.id}`)}),
        ]}
        handleReload={() => searchClientsServices({filters, tablePagination, sort: sortModel})}
        handlePageSizeChange={clientsServicesService.setPagination('limit')}
        pageSize={tablePagination.limit}
        handlePageChange={(page) => clientsServicesService.setPagination('page')(page + 1)}
        page={tablePagination.page - 1}
        handleSortModelChange={clientsServicesService.setSort}
        sortModel={[sortModel]}
        handleRowSelected={clientsServicesService.setSelectedClientsServices}
        rowsSelected={selectedClientsServices}
        totalRows={clientsServicesQuery.totalRows}
        data={clientsServices}/>
      <Outlet/>
    </Stack>
  );
};

export default ClientContractedServicesTable;
